/**
 * The minigames' common ground (owner, Sep 28: "build some minigames: if a player has game console selected and uses it on any tv, the
 * tv should open with a few selectable minigames * some mobile minigames that open up when a player reaches some achievements").
 *
 * A game is a module (`MiniGame`): an id, a name, an icon, where it plays (`tv` — the console at a TV — or `phone` — the Arcade app),
 * the unlock id a phone game needs, and three functions: `init(seed)` makes its state, `step(state, input, dt)` moves it on, `draw`
 * paints it. The state is plain data and the step is a pure function of it, the input and dt (randomness from `rnd`, whose state is
 * `s.r`) — so a seed and a script of inputs always play out the same (tests/arcade.test.ts), and the host (arcade-view.ts) only
 * feeds it keys, taps and a fixed 60 Hz clock. The step also leaves what it wants heard in `s.fx`, which the host plays through the
 * game's audio and empties.
 *
 * Every game draws in its own logical pixels (`w` × `h`: 320 × 180 on a TV, 180 × 320 on the phone) and the host scales it to fit.
 * The look is the game's comic one: flat bright colours, heavy black outlines, halftone dots, POW! bursts (`draw.ts`).
 */

/** what a game can ask the speaker for (engine/audio.ts `arcade`) */
export type Sfx = 'blip' | 'coin' | 'hit' | 'shoot' | 'boom' | 'flap' | 'lose' | 'win' | 'beat' | 'miss' | 'punch' | 'turn' | 'line' | 'honk' | 'click';
export const SFX: readonly Sfx[] = ['blip', 'coin', 'hit', 'shoot', 'boom', 'flap', 'lose', 'win', 'beat', 'miss', 'punch', 'turn', 'line', 'honk', 'click'];

/** the direction and button bits of `press` / `swipe` */
export const L = 1, R = 2, U = 4, D = 8, A = 16;

/** one step's input, the same shape from a keyboard, a mouse or a finger */
export interface MiniInput {
  /** held directions, −1..1 (arrows / WASD) */
  x: number; y: number;
  /** the action button held (Space, Enter, Z) */
  a: boolean;
  /** what went down this step: L R U D A bits (a key's press, or a swipe) */
  press: number;
  /** swipes alone this step (the same bits; a game that treats a swipe apart from a key — the stacker's drop — reads this) */
  swipe: number;
  /** the pointer in the game's pixels (a mouse hovering counts), or −1 when there is none */
  px: number; py: number;
  /** the pointer is down (a finger on the glass, the mouse button) */
  pdown: boolean;
  /** a tap / click landed this step (at px, py) */
  ptap: boolean;
}
export const NO_INPUT: MiniInput = Object.freeze({ x: 0, y: 0, a: false, press: 0, swipe: 0, px: -1, py: -1, pdown: false, ptap: false });
export function input(o: Partial<MiniInput>): MiniInput { return { ...NO_INPUT, ...o }; }

/** the host's fixed step: 60 a second, whatever the screen's rate */
export const STEP = 1 / 60;
/** what came in between frames that happens once: a tap / click (where), key presses and swipes (bits) */
export interface Edges { tap?: { x: number; y: number }; press: number; swipe: number }
/** (owner, Sep 28: "the racer tracks the mouse and the keys instantly, but in the other games a click sometimes registers and sometimes
 *  not") a frame of the host's clock: `dt` joins the carried `acc`, `step(first, edges)` runs once per whole STEP in it until it says
 *  stop (the game is over), and the edges go to the first step. On a 90–144 Hz screen many frames are too short to step — the edges
 *  then stay in `q` for the next frame instead of being dropped (the racer only reads what is held, so it never lost one). Returns the
 *  new carry. */
export function clock(acc: number, dt: number, q: Edges, step: (first: boolean, e: Edges) => boolean): number {
  acc += dt; if (acc < STEP) return acc;
  const e: Edges = { tap: q.tap, press: q.press, swipe: q.swipe }; q.tap = undefined; q.press = 0; q.swipe = 0;
  for (let first = true; acc >= STEP; first = false) { acc -= STEP; if (!step(first, e)) break; }
  return acc;
}

/** a comic burst over the play ("POW!", "+50"): where, what, how long it has been up */
export interface Pop { x: number; y: number; text: string; t: number; color: string }
/** what every game's state carries */
export interface Core {
  score: number;
  /** the game has ended (the host shows the result and sends the score) */
  over: boolean;
  /** seconds played */
  t: number;
  /** the random state (rnd) */
  r: number;
  /** sounds asked for since the host last emptied it */
  fx: Sfx[];
  pops: Pop[];
  /** lives left, where a game has them (the HUD's hearts) */
  lives?: number;
  /** the screen shakes this much (game pixels), decaying */
  shake?: number;
}
export type Where = 'tv' | 'phone';
export interface MiniGame<S extends Core = Core> {
  id: string;
  name: string;
  icon: string;
  /** the tile's colour in the menus */
  color: string;
  where: Where;
  /** a phone game opens up with this unlock (the achievements grant it — content/achievements) */
  unlock?: string;
  /** one line on what it is */
  blurb: string;
  /** how to play, by keys and by touch */
  keys: string; touch: string;
  /** the logical canvas */
  w: number; h: number;
  /** the most points a second of honest play can make, and the most a game can make at all (the server clamps a sent score to them) */
  maxRate: number; maxScore: number;
  init(seed: number): S;
  step(s: S, inp: MiniInput, dt: number): void;
  draw(ctx: CanvasRenderingContext2D, s: S): void;
}
/** a game of any state, as the lists hold them */
export type AnyGame = MiniGame<any>; // eslint-disable-line @typescript-eslint/no-explicit-any

/** the next random number in [0, 1) from the state's own generator (mulberry32 — small, and its whole state is one number) */
export function rnd(s: { r: number }): number {
  let t = (s.r = (s.r + 0x6d2b79f5) >>> 0);
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
}
/** an integer in [a, b] */
export function rint(s: { r: number }, a: number, b: number): number { return a + Math.floor(rnd(s) * (b - a + 1)); }
/** the common state for a seed */
export function core(seed: number, lives?: number): Core { return { score: 0, over: false, t: 0, r: (seed >>> 0) || 1, fx: [], pops: [], ...(lives !== undefined ? { lives } : {}) }; }
/** a burst over the play */
export function pop(s: Core, x: number, y: number, text: string, color = '#ffe14a'): void { s.pops.push({ x, y, text, t: 0, color }); if (s.pops.length > 12) s.pops.shift(); }
/** the common part of every step: the clock, the bursts ageing, the shake settling */
export function tick(s: Core, dt: number): void {
  s.t += dt;
  for (const p of s.pops) p.t += dt;
  s.pops = s.pops.filter((p) => p.t < POP_S);
  if (s.shake) s.shake = s.shake < 0.2 ? 0 : s.shake * Math.pow(0.02, dt);
}
export const POP_S = 0.8;
/** the game ends: the sound, and never twice */
export function end(s: Core, fx: Sfx = 'lose'): void { if (s.over) return; s.over = true; s.fx.push(fx); }
/** a life lost; the game ends on the last */
export function hurt(s: Core, shake = 4): void { s.lives = Math.max(0, (s.lives ?? 1) - 1); s.shake = shake; s.fx.push('hit'); if (s.lives <= 0) end(s); }
export const clamp = (v: number, a: number, b: number): number => (v < a ? a : v > b ? b : v);

/** run a game headless: a script of inputs (one per step, or a function of the state) at 60 Hz for `secs`, or until it ends */
export function simulate<S extends Core>(g: MiniGame<S>, seed: number, secs: number, script: (s: S, i: number) => MiniInput = () => NO_INPUT): S {
  const s = g.init(seed), dt = 1 / 60;
  for (let i = 0; i < secs * 60 && !s.over; i++) { g.step(s, script(s, i), dt); s.fx.length = 0; }
  return s;
}
